/**
 * Epic #228 P6: media framing. Where an authored picture sits in its frame,
 * per device, and the one place that turns a frame into CSS.
 *
 * Model (PD1): `x`/`y` are the CSS `object-position` fractions on both axes
 * and are physical (never mirrored in RTL). `zoom` is relative to the cover
 * scale. `object-position: x% y%` plus `transform: scale(zoom)` around the
 * same origin renders a cover-sized image of `zoom ×` the cover size placed
 * at `x`/`y`, so every in-range frame covers its box: keeping the frame
 * covered is clamping, never a geometry solver.
 *
 * Imports types and `./internal` only (no import cycle).
 */
import type {
  WebsiteFrameDevice,
  WebsiteIssueCode,
  WebsiteLayoutFamily,
  WebsiteMediaAspect,
  WebsiteMediaFrame,
  WebsiteMediaFraming,
  WebsiteMediaValue,
} from '../../types/website-templates';
import { checkClosedKeys, isRecord } from './internal';

/** Closed bounds of one frame. SQL `website_media_framing_issue` mirrors them. */
export const WEBSITE_MEDIA_FRAME_LIMITS = {
  x: [0, 1],
  y: [0, 1],
  zoom: [1, 4],
  decimals: 4,
} as const;

/** The viewport query that makes a page `desktop` for framing (same as the Luxury/Beauty heroes). */
export const WEBSITE_FRAME_DESKTOP_QUERY = '(min-width: 1024px)';

/** The keys of a `WebsiteMediaFraming`, in the order the validators walk them. */
export const WEBSITE_MEDIA_FRAMING_KEYS = ['shared', 'desktop', 'mobile'] as const;
export type WebsiteMediaFramingKey = (typeof WEBSITE_MEDIA_FRAMING_KEYS)[number];

const FRAME_KEYS = ['x', 'y', 'zoom'] as const;

/** The CSS default of `object-position` (`50% 50%`) at cover scale. */
export const WEBSITE_MEDIA_FRAME_CENTER: WebsiteMediaFrame = { x: 0.5, y: 0.5, zoom: 1 };

function isFrameNumber(value: unknown, min: number, max: number): boolean {
  // Exact decimal rule (PD10): SQL checks `v = round(v, 4)` on the jsonb numeric.
  return (
    typeof value === 'number' &&
    Number.isFinite(value) &&
    value >= min &&
    value <= max &&
    Number(value.toFixed(WEBSITE_MEDIA_FRAME_LIMITS.decimals)) === value
  );
}

function frameIssue(value: unknown): WebsiteIssueCode | null {
  if (!isRecord(value)) return 'invalid_slot';
  const issue = checkClosedKeys(value, FRAME_KEYS);
  if (issue) return issue;
  const [xMin, xMax] = WEBSITE_MEDIA_FRAME_LIMITS.x;
  const [yMin, yMax] = WEBSITE_MEDIA_FRAME_LIMITS.y;
  const [zoomMin, zoomMax] = WEBSITE_MEDIA_FRAME_LIMITS.zoom;
  return isFrameNumber(value.x, xMin, xMax) &&
    isFrameNumber(value.y, yMin, yMax) &&
    isFrameNumber(value.zoom, zoomMin, zoomMax)
    ? null
    : 'invalid_slot';
}

/**
 * Validates the `framing` key of a media value. `null` when valid, else the
 * issue code. Fixture: `__fixtures__/media-framing.json`.
 *
 * Rules: an object with closed keys `shared|desktop|mobile` and at least one
 * present; each frame an object with closed keys `x|y|zoom`, all JSON
 * numbers within `WEBSITE_MEDIA_FRAME_LIMITS` with at most 4 decimals.
 *
 * Codes: a structural key (`style`, `src`, ...) at the framing level or at
 * any frame level gives `structure_override`, wherever it sits, because it
 * outranks every other finding; every other defect gives `invalid_slot`. So
 * the result never depends on key order. Keys are read at those two levels
 * only: a non-number in a number position is `invalid_slot` and is not
 * scanned further.
 */
export function validateWebsiteMediaFraming(value: unknown): WebsiteIssueCode | null {
  if (!isRecord(value)) return 'invalid_slot';
  const topIssue = checkClosedKeys(value, WEBSITE_MEDIA_FRAMING_KEYS, []);
  if (topIssue === 'structure_override') return topIssue;
  let issue: WebsiteIssueCode | null = topIssue;
  let present = 0;
  for (const key of WEBSITE_MEDIA_FRAMING_KEYS) {
    if (!Object.prototype.hasOwnProperty.call(value, key)) continue;
    present += 1;
    const found = frameIssue(value[key]);
    if (found === 'structure_override') return found;
    issue ??= found;
  }
  if (issue) return issue;
  return present === 0 ? 'invalid_slot' : null;
}

/** PD1: `framing[device] → framing.shared → null`. `null` = the family CSS default. */
export function resolveWebsiteMediaFrame(
  framing: WebsiteMediaFraming | undefined,
  device: WebsiteFrameDevice,
): WebsiteMediaFrame | null {
  return framing?.[device] ?? framing?.shared ?? null;
}

function quantizeAxis(value: number, min: number, max: number, fallback: number): number {
  if (!Number.isFinite(value)) return fallback;
  const clamped = Math.min(max, Math.max(min, value));
  // `+ 0` turns -0 into 0.
  return Number(clamped.toFixed(WEBSITE_MEDIA_FRAME_LIMITS.decimals)) + 0;
}

/**
 * Clamps a frame into `WEBSITE_MEDIA_FRAME_LIMITS` and rounds every number
 * to 4 decimals, so the result always passes `validateWebsiteMediaFraming`.
 * A non-finite axis becomes the center (`x`/`y` 0.5, `zoom` 1).
 */
export function quantizeWebsiteMediaFrame(frame: WebsiteMediaFrame): WebsiteMediaFrame {
  const [xMin, xMax] = WEBSITE_MEDIA_FRAME_LIMITS.x;
  const [yMin, yMax] = WEBSITE_MEDIA_FRAME_LIMITS.y;
  const [zoomMin, zoomMax] = WEBSITE_MEDIA_FRAME_LIMITS.zoom;
  return {
    x: quantizeAxis(frame.x, xMin, xMax, WEBSITE_MEDIA_FRAME_CENTER.x),
    y: quantizeAxis(frame.y, yMin, yMax, WEBSITE_MEDIA_FRAME_CENTER.y),
    zoom: quantizeAxis(frame.zoom, zoomMin, zoomMax, WEBSITE_MEDIA_FRAME_CENTER.zoom),
  };
}

/**
 * The inline style of a framed `<img>` / `<video>`. Structurally a React
 * `CSSProperties`; `transform` is absent at zoom 1.
 */
export interface WebsiteMediaFrameStyle {
  readonly objectPosition: string;
  readonly transformOrigin: string;
  readonly transform?: string;
}

/** A 4-decimal fraction as an exact 2-decimal percentage (`0.1234` → `12.34%`). */
function percent(fraction: number): string {
  return `${Math.round(fraction * 10_000) / 100}%`;
}

/**
 * THE emitter (design §4.2): the only code that turns a frame into CSS. Used
 * by the Storefront `WebsiteMedia` and the Admin replica, so both render the
 * same strings. `null` → `undefined` (no inline style: the family CSS
 * default applies, today's DOM).
 */
export function websiteMediaFrameStyle(
  frame: WebsiteMediaFrame | null,
): WebsiteMediaFrameStyle | undefined {
  if (frame === null) return undefined;
  const { x, y, zoom } = quantizeWebsiteMediaFrame(frame);
  const position = `${percent(x)} ${percent(y)}`;
  return zoom === 1
    ? { objectPosition: position, transformOrigin: position }
    : { objectPosition: position, transformOrigin: position, transform: `scale(${zoom})` };
}

/**
 * Sets (`frame`) or removes (`null`) one key of a framing. Returns
 * `undefined` when no key is left: a stored `framing` is never `{}` (PD6).
 */
export function setWebsiteMediaFrame(
  framing: WebsiteMediaFraming | undefined,
  key: WebsiteMediaFramingKey,
  frame: WebsiteMediaFrame | null,
): WebsiteMediaFraming | undefined {
  const next: { -readonly [K in WebsiteMediaFramingKey]?: WebsiteMediaFrame } = {};
  for (const name of WEBSITE_MEDIA_FRAMING_KEYS) {
    const current = name === key ? frame : (framing?.[name] ?? null);
    if (current !== null) next[name] = quantizeWebsiteMediaFrame(current);
  }
  return Object.keys(next).length === 0 ? undefined : next;
}

/** A media value with `framing` replaced; the key is omitted when `framing` is undefined. */
export function websiteMediaValueWithFraming(
  value: WebsiteMediaValue,
  framing: WebsiteMediaFraming | undefined,
): WebsiteMediaValue {
  const { framing: _previous, ...rest } = value;
  return framing === undefined ? rest : { ...rest, framing };
}

// ---------------------------------------------------------------------------
// Frame registry (S2). The real rendered frame of every media slot, per
// layout family and device, authored from the family CSS and components.
// Renderer-side on purpose: manifests are immutable and their `mediaAspect`
// is not the rendered frame (audit/02 R4).
// ---------------------------------------------------------------------------

/** Text direction of the page the frame renders in. */
export type WebsiteFrameDirection = 'ltr' | 'rtl';

/** A value that may differ between LTR and RTL pages (family `:dir(rtl)` rules). */
export interface WebsiteFrameByDirection<T> {
  readonly ltr: T;
  readonly rtl: T;
}

/** The rendered frame of one slot on one device. */
export interface WebsiteMediaFrameGeometry {
  /**
   * Width / height of the rendered frame, at 390 px (mobile) or in the
   * 1280 px canvas frame (desktop), per direction.
   */
  readonly aspect: WebsiteFrameByDirection<number>;
  /**
   * The frame depends on the viewport or on the copy beside it (a
   * `min-height` panel, a 60 % column): the aspect is the reference value at
   * 390 / 1280 and the Admin replica is labelled a preview.
   */
  readonly approximate: boolean;
  /** The family CSS `object-position` of an unframed `<img>` (PD7), per direction. */
  readonly defaultFrame: WebsiteFrameByDirection<WebsiteMediaFrame>;
  /**
   * Present when the family rule targets `<img>` only, so an unframed
   * `<video>` shows the CSS initial value instead. Absent = `defaultFrame`.
   */
  readonly videoDefaultFrame?: WebsiteFrameByDirection<WebsiteMediaFrame>;
}

/** The registry entry of one media slot. */
export interface WebsiteMediaFrameEntry {
  /**
   * Where the slot renders. `desktop`: only at `WEBSITE_FRAME_DESKTOP_QUERY`
   * (a hero `desktopMedia`; its `mobile` geometry repeats `desktop`).
   */
  readonly device: WebsiteFrameDevice | 'any';
  /**
   * PD2: the frame aspect differs by more than 5 % between 390 and 1280, so
   * the editor writes `desktop` and `mobile` from two tabs. Otherwise it
   * writes `shared` only.
   */
  readonly perDevice: boolean;
  /** `false`: no registry entry, the `mediaAspect` fallback (center, approximate). */
  readonly explicit: boolean;
  readonly desktop: WebsiteMediaFrameGeometry;
  readonly mobile: WebsiteMediaFrameGeometry;
}

/** PD2/PD5: the largest relative aspect difference that still counts as one frame. */
export const WEBSITE_MEDIA_FRAME_PER_DEVICE_TOLERANCE = 0.05;

const ratio = (width: number, height: number): number => Number((width / height).toFixed(4));
const at = (x: number, y: number): WebsiteMediaFrame => ({ x, y, zoom: 1 });
const both = <T>(value: T): WebsiteFrameByDirection<T> => ({ ltr: value, rtl: value });

interface GeometryInput {
  readonly aspect: number | WebsiteFrameByDirection<number>;
  readonly approximate?: boolean;
  readonly defaultFrame?: WebsiteMediaFrame | WebsiteFrameByDirection<WebsiteMediaFrame>;
  /** The family rule targets `<img>` only; `<video>` keeps the CSS center. */
  readonly imageOnlyDefault?: boolean;
}

function byDirection<T extends object>(
  value: T | WebsiteFrameByDirection<T>,
): WebsiteFrameByDirection<T> {
  return 'ltr' in value && 'rtl' in value
    ? (value as WebsiteFrameByDirection<T>)
    : both(value as T);
}

function geometry(input: GeometryInput): WebsiteMediaFrameGeometry {
  return {
    aspect: typeof input.aspect === 'number' ? both(input.aspect) : input.aspect,
    approximate: input.approximate === true,
    defaultFrame: byDirection(input.defaultFrame ?? WEBSITE_MEDIA_FRAME_CENTER),
    ...(input.imageOnlyDefault === true
      ? { videoDefaultFrame: both(WEBSITE_MEDIA_FRAME_CENTER) }
      : {}),
  };
}

interface FrameRule {
  readonly slot: RegExp;
  /** Only for slots that never render at mobile (a hero `desktopMedia`). */
  readonly desktopOnly?: true;
  readonly desktop: WebsiteMediaFrameGeometry;
  readonly mobile: WebsiteMediaFrameGeometry;
}

/** Every family's `storeInfo.info.media` (`store-info-page.tsx`: `aspect-[4/5]`). */
const STORE_INFO: FrameRule = {
  slot: /^storeInfo\.info\.media$/,
  desktop: geometry({ aspect: 0.8 }),
  mobile: geometry({ aspect: 0.8 }),
};

// Luxury hero (`luxury-jewelry.css` .luxury-hero-media): mobile `aspect-ratio:
// 3/4`, img `center top`; ≥1024 a 60 % wide, full-height panel (hero ≥620 px)
// with img `right center` in both directions (only the gradient flips).
const LUXURY_HERO_DESKTOP = geometry({
  aspect: ratio(768, 620),
  approximate: true,
  defaultFrame: at(1, 0.5),
  imageOnlyDefault: true,
});
// Beauty hero (`beauty.css` .beauty-hero-media): `3 / 4`, ≥1024 `16 / 9`, center.
const BEAUTY_HERO_DESKTOP = geometry({ aspect: ratio(16, 9) });

const FAMILY_FRAMES: Readonly<Record<WebsiteLayoutFamily, readonly FrameRule[]>> = {
  // `atelier-blocks.tsx`.
  atelier: [
    {
      // Carousel slide: `absolute inset-0` of a `min-h-[22rem] md:min-h-[30rem]` slide.
      slot: /^home\.hero\.slide[1-9][0-9]*\.media$/,
      desktop: geometry({ aspect: ratio(1280, 480), approximate: true }),
      mobile: geometry({ aspect: ratio(390, 352), approximate: true }),
    },
    {
      // `aspect-[4/5]` beside the copy.
      slot: /^home\.promo\.media$/,
      desktop: geometry({ aspect: 0.8 }),
      mobile: geometry({ aspect: 0.8 }),
    },
    STORE_INFO,
  ],
  // `stride-blocks.tsx`.
  stride: [
    {
      // Full bleed: `absolute inset-0` of a `min-h-[26rem] md:min-h-[36rem]` section.
      slot: /^home\.hero\.media$/,
      desktop: geometry({ aspect: ratio(1280, 576), approximate: true }),
      mobile: geometry({ aspect: ratio(390, 416), approximate: true }),
    },
    {
      // Split promo: `aspect-square` (the manifest says 4:5; the frame is 1:1).
      slot: /^home\.promo[AB]\.media$/,
      desktop: geometry({ aspect: 1 }),
      mobile: geometry({ aspect: 1 }),
    },
    STORE_INFO,
  ],
  // `editorial-fashion-blocks.tsx` + `website.css` (.fashion-hero-media).
  'editorial-fashion': [
    {
      // `4 / 5`, ≥1024 `21 / 9`. Authored media is centered; only the shipped
      // template hero asset gets `object-top`.
      slot: /^home\.hero\.media$/,
      desktop: geometry({ aspect: ratio(21, 9) }),
      mobile: geometry({ aspect: 0.8 }),
    },
    {
      // Category campaign: `aspect-[16/10] md:aspect-[21/9]`.
      slot: /^home\.campaign[1-9][0-9]*\.media$/,
      desktop: geometry({ aspect: ratio(21, 9) }),
      mobile: geometry({ aspect: 1.6 }),
    },
    {
      // About teaser: `aspect-[4/3] md:aspect-[4/5]`.
      slot: /^home\.about\.media$/,
      desktop: geometry({ aspect: 0.8 }),
      mobile: geometry({ aspect: ratio(4, 3) }),
    },
    STORE_INFO,
  ],
  // `beauty-home-blocks.tsx`, `blocks/website-community.tsx` + `beauty.css`.
  beauty: [
    {
      // Also the desktop picture while `desktopMedia` is empty.
      slot: /^home\.hero\.media$/,
      desktop: BEAUTY_HERO_DESKTOP,
      mobile: geometry({ aspect: 0.75 }),
    },
    {
      slot: /^home\.hero\.desktopMedia$/,
      desktopOnly: true,
      desktop: BEAUTY_HERO_DESKTOP,
      mobile: BEAUTY_HERO_DESKTOP,
    },
    {
      // `.beauty-community-media`: `aspect-ratio: 3 / 4`.
      slot: /^home\.community\.item[1-9][0-9]*\.media$/,
      desktop: geometry({ aspect: 0.75 }),
      mobile: geometry({ aspect: 0.75 }),
    },
    STORE_INFO,
  ],
  // `luxury-home-blocks.tsx` + `luxury-jewelry.css`.
  'luxury-jewelry': [
    {
      // Also the desktop panel while `desktopMedia` is empty.
      slot: /^home\.hero\.media$/,
      desktop: LUXURY_HERO_DESKTOP,
      mobile: geometry({ aspect: 0.75, defaultFrame: at(0.5, 0), imageOnlyDefault: true }),
    },
    {
      slot: /^home\.hero\.desktopMedia$/,
      desktopOnly: true,
      desktop: LUXURY_HERO_DESKTOP,
      mobile: LUXURY_HERO_DESKTOP,
    },
    {
      // Campaign: `inset: 0` of a section ≥480 px (≥540 px at 1024), img `80%
      // center`. In RTL at ≥1024 the media is a 50 % panel with img `right
      // center` (`:dir(rtl)` rules).
      slot: /^home\.campaign\.media$/,
      desktop: geometry({
        aspect: { ltr: ratio(1280, 540), rtl: ratio(640, 540) },
        approximate: true,
        defaultFrame: { ltr: at(0.8, 0.5), rtl: at(1, 0.5) },
        imageOnlyDefault: true,
      }),
      mobile: geometry({
        aspect: ratio(390, 480),
        approximate: true,
        defaultFrame: at(0.8, 0.5),
        imageOnlyDefault: true,
      }),
    },
    {
      // Story: `.luxury-story .luxury-editorial-media` `aspect-ratio: 4/5`.
      slot: /^home\.story[1-9][0-9]*\.media$/,
      desktop: geometry({ aspect: 0.8 }),
      mobile: geometry({ aspect: 0.8 }),
    },
    STORE_INFO,
  ],
  // Frozen legacy designs render through the engine renderer, not a family.
  'legacy-engine-v2': [],
};

function aspectDiffers(left: number, right: number): boolean {
  return Math.abs(left / right - 1) > WEBSITE_MEDIA_FRAME_PER_DEVICE_TOLERANCE;
}

function mediaAspectRatio(mediaAspect: WebsiteMediaAspect | undefined): number {
  if (mediaAspect === undefined) return 1;
  const [width, height] = mediaAspect.split(':').map(Number);
  return width && height ? ratio(width, height) : 1;
}

/**
 * The registry entry of one media slot (design §4.2). A slot without an
 * entry (a retired semantic such as category tile images, or a template
 * the registry does not know yet) falls back to its `mediaAspect` on both
 * devices, centered, `approximate`, `perDevice: false`, `explicit: false`.
 */
export function websiteMediaFrame(
  layoutFamily: WebsiteLayoutFamily,
  slotId: string,
  mediaAspect?: WebsiteMediaAspect,
): WebsiteMediaFrameEntry {
  const rule = FAMILY_FRAMES[layoutFamily].find((candidate) => candidate.slot.test(slotId));
  if (!rule) {
    const fallback = geometry({ aspect: mediaAspectRatio(mediaAspect), approximate: true });
    return {
      device: 'any',
      perDevice: false,
      explicit: false,
      desktop: fallback,
      mobile: fallback,
    };
  }
  const perDevice =
    rule.desktopOnly !== true &&
    (aspectDiffers(rule.desktop.aspect.ltr, rule.mobile.aspect.ltr) ||
      aspectDiffers(rule.desktop.aspect.rtl, rule.mobile.aspect.rtl));
  return {
    device: rule.desktopOnly === true ? 'desktop' : 'any',
    perDevice,
    explicit: true,
    desktop: rule.desktop,
    mobile: rule.mobile,
  };
}

/** The devices the editor offers for a slot: two tabs when `perDevice`, else one. */
export function websiteMediaFrameDevices(
  entry: WebsiteMediaFrameEntry,
): readonly WebsiteFrameDevice[] {
  if (entry.perDevice) return ['desktop', 'mobile'];
  return [entry.device === 'mobile' ? 'mobile' : 'desktop'];
}

/** PD2: the framing key the editor writes for a device tab. */
export function websiteMediaFramingKeyFor(
  entry: WebsiteMediaFrameEntry,
  device: WebsiteFrameDevice,
): WebsiteMediaFramingKey {
  return entry.perDevice ? device : 'shared';
}

/**
 * What an unframed slot shows on a device (PD7): the family CSS default for
 * that direction, or the CSS center for a `<video>` when the family rule
 * targets `<img>` only.
 */
export function websiteMediaDefaultFrame(
  geometry: WebsiteMediaFrameGeometry,
  direction: WebsiteFrameDirection,
  element: 'image' | 'video' = 'image',
): WebsiteMediaFrame {
  const frames =
    element === 'video'
      ? (geometry.videoDefaultFrame ?? geometry.defaultFrame)
      : geometry.defaultFrame;
  return frames[direction];
}

// ---------------------------------------------------------------------------
// Editor math (S2). Pure; every result is quantized.
// ---------------------------------------------------------------------------

export interface WebsiteMediaFrameSize {
  readonly width: number;
  readonly height: number;
}

/** Below this overflow (CSS px) an axis cannot move: the picture exactly fills it. */
const LOCKED_AXIS_PX = 0.5;

/**
 * Drags the picture by `delta` (CSS px inside a `box`-sized replica). The
 * picture follows the pointer: `dx > 0` moves it right, so `x` falls. The
 * rendered picture is `cover(natural, box) × zoom`; an axis whose overflow
 * is (about) 0 is locked. Sizes that are not positive leave the frame as it
 * is (quantized).
 */
export function dragWebsiteMediaFrame(
  frame: WebsiteMediaFrame,
  delta: { readonly dx: number; readonly dy: number },
  box: WebsiteMediaFrameSize,
  natural: WebsiteMediaFrameSize,
): WebsiteMediaFrame {
  const current = quantizeWebsiteMediaFrame(frame);
  if (!(box.width > 0 && box.height > 0 && natural.width > 0 && natural.height > 0)) {
    return current;
  }
  const cover = Math.max(box.width / natural.width, box.height / natural.height);
  const overflowX = natural.width * cover * current.zoom - box.width;
  const overflowY = natural.height * cover * current.zoom - box.height;
  const dx = Number.isFinite(delta.dx) ? delta.dx : 0;
  const dy = Number.isFinite(delta.dy) ? delta.dy : 0;
  return quantizeWebsiteMediaFrame({
    x: overflowX > LOCKED_AXIS_PX ? current.x - dx / overflowX : current.x,
    y: overflowY > LOCKED_AXIS_PX ? current.y - dy / overflowY : current.y,
    zoom: current.zoom,
  });
}

/** Keyboard steps: 1 % with an arrow, 5 % with Shift+arrow. */
export type WebsiteMediaFrameNudgeStep = 0.01 | 0.05;

/**
 * Arrow keys move the picture in the arrow's PHYSICAL direction, also in
 * RTL (PD1): Right moves it right (`x` falls), Down moves it down (`y` falls).
 */
export function nudgeWebsiteMediaFrame(
  frame: WebsiteMediaFrame,
  direction: 'left' | 'right' | 'up' | 'down',
  step: WebsiteMediaFrameNudgeStep,
): WebsiteMediaFrame {
  const current = quantizeWebsiteMediaFrame(frame);
  switch (direction) {
    case 'left':
      return quantizeWebsiteMediaFrame({ ...current, x: current.x + step });
    case 'right':
      return quantizeWebsiteMediaFrame({ ...current, x: current.x - step });
    case 'up':
      return quantizeWebsiteMediaFrame({ ...current, y: current.y + step });
    case 'down':
      return quantizeWebsiteMediaFrame({ ...current, y: current.y - step });
  }
}

/** Sets the zoom, clamped to `[1, 4]` (never below cover); `x`/`y` stay. */
export function zoomWebsiteMediaFrame(frame: WebsiteMediaFrame, zoom: number): WebsiteMediaFrame {
  return quantizeWebsiteMediaFrame({ ...frame, zoom });
}
